/**
 * Switch Component
 * Toggle switch input
 */

import { forwardRef } from 'react';

export interface SwitchProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
    label?: string;
    description?: string;
    size?: 'sm' | 'md' | 'lg';
}

export const Switch = forwardRef<HTMLInputElement, SwitchProps>(
    ({ label, description, size = 'md', className = '', disabled, ...props }, ref) => {
        const sizeConfig = {
            sm: {
                track: 'w-8 h-4',
                thumb: 'w-3 h-3',
                translate: 'translate-x-4',
            },
            md: {
                track: 'w-11 h-6',
                thumb: 'w-5 h-5',
                translate: 'translate-x-5',
            },
            lg: {
                track: 'w-14 h-7',
                thumb: 'w-6 h-6',
                translate: 'translate-x-7',
            },
        };

        const config = sizeConfig[size];

        return (
            <label className={`flex items-start gap-3 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${className}`}>
                <div className="relative flex items-center">
                    <input
                        ref={ref}
                        type="checkbox"
                        disabled={disabled}
                        className="peer sr-only"
                        {...props}
                    />
                    <div
                        className={`
                            ${config.track} rounded-full transition-colors
                            peer-focus:ring-2 peer-focus:ring-primary peer-focus:ring-offset-2
                            ${disabled ? 'bg-gray-200' : 'bg-gray-300 peer-checked:bg-primary'}
                        `}
                    />
                    <div
                        className={`
                            absolute left-0.5 ${config.thumb} bg-white rounded-full shadow
                            transition-transform peer-checked:${config.translate}
                        `}
                    />
                </div>
                {(label || description) && (
                    <div className="flex-1">
                        {label && (
                            <span className={`text-sm font-medium ${disabled ? 'text-gray-400' : 'text-gray-700'}`}>
                                {label}
                            </span>
                        )}
                        {description && (
                            <p className={`text-sm ${disabled ? 'text-gray-300' : 'text-gray-500'}`}>
                                {description}
                            </p>
                        )}
                    </div>
                )}
            </label>
        );
    }
);

Switch.displayName = 'Switch';

export default Switch;
